import React, { useEffect, useRef } from "react";
import usePipelinesApi, { StageItem } from "../Pipeline/use-pipelines";
import { useToast } from "../..";
import { Container, ListStages } from "./styles";
import { Button } from "../..";
import { InputText } from "primereact/inputtext";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { v } from "../../../util";
import { AuthContextType, useAuth } from "../../../auth/auth-provider";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult
} from "@hello-pangea/dnd";

type UpdateStageProps = {
  stages: StageItem[];
  onClose: () => void;
  onSuccess: () => void;
  pipelineId: number;
};

type DraftStageItem = {
  stageId?: number;
  tempId: string;
  name: string;
  color: string;
  position: number;
  isNew: boolean;
};

type UpdateStageFormValues = {
  stages: DraftStageItem[];
};

const updateStageSchema = v.object({
  stages: v.array(
    v.object({
      stageId: v.number().optional(),
      tempId: v.string(),
      name: v.string().superRefine(v.nonempty()),
      color: v.string(),
      position: v.number(),
      isNew: v.boolean()
    })
  )
});

const UpdateStage: React.FC<UpdateStageProps> = ({
  stages,
  onClose,
  onSuccess,
  pipelineId
}) => {
  const toast = useToast();
  const { deleteStage, saveStages } = usePipelinesApi();
  const { user, company, hasAnyPermission } = useAuth() as AuthContextType;

  const colorInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<UpdateStageFormValues>({
    defaultValues: {
      stages: []
    },
    resolver: zodResolver(updateStageSchema),
    shouldFocusError: true
  });

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "stages"
  });

  const getRandomSuggestedColor = () => {
    const colors = [
      "#6B7280",
      "#2563EB",
      "#F59E0B",
      "#10B981",
      "#EF4444",
      "#8B5CF6",
      "#14B8A6"
    ];

    return colors[Math.floor(Math.random() * colors.length)];
  };

  const startCreateStage = () => {
    append({
      tempId: `${Date.now()}-${Math.random()}`,
      name: "",
      color: getRandomSuggestedColor(),
      position: fields.length + 1,
      isNew: true
    });
  };

  const openColorPicker = (tempId: string) => {
    setTimeout(() => {
      const input = colorInputRefs.current[tempId] as
        | (HTMLInputElement & { showPicker?: () => void })
        | null;

      if (!input) return;

      if (typeof input.showPicker === "function") {
        input.showPicker();
      } else {
        input.click();
      }
    }, 0);
  };

  const handleRemoveStage = async (index: number) => {
    const stage = fields[index];

    if (stage.isNew) {
      remove(index);
      return;
    }

    if (!stage.stageId) {
      return;
    }

    try {
      await deleteStage(stage.stageId);

      toast?.current?.show({
        life: 5000,
        severity: "success",
        summary: "Sucesso",
        detail: "Etapa excluída com sucesso."
      });

      remove(index);
      onSuccess();
    } catch (error: any) {
      const backendMessage =
        error?.message ||
        error?.response?.data?.message ||
        "Erro ao excluir etapa.";

      toast?.current?.show({
        life: 5000,
        severity: "error",
        summary: "Erro",
        detail: backendMessage
      });
    }
  };

  const hasStageChanges = (currentStages: DraftStageItem[]) => {
    if (currentStages.length !== stages.length) {
      return true;
    }

    const originalStagesSorted = [...stages]
      .map((item, index) => ({
        stageId: item.id,
        name: item.name ?? "",
        color: item.color || "#6B7280",
        position: item.position ?? index + 1
      }))
      .sort((a, b) => (a.stageId ?? 0) - (b.stageId ?? 0));

    const currentStagesSorted = [...currentStages]
      .map((item, index) => ({
        stageId: item.stageId,
        name: item.name.trim(),
        color: item.color || "#6B7280",
        position: index + 1
      }))
      .sort((a, b) => (a.stageId ?? 0) - (b.stageId ?? 0));

    return (
      JSON.stringify(originalStagesSorted) !==
      JSON.stringify(currentStagesSorted)
    );
  };

  const handleSaveAll = handleSubmit(async (data) => {
    try {
      const canEditPipeline = user.admin || hasAnyPermission("pipeline_edit");

      if (!canEditPipeline) {
        toast?.current?.show({
          life: 5000,
          severity: "warn",
          summary: "Aviso",
          detail: "Você não tem permissão para editar etapas."
        });
        return;
      }

      if (!hasStageChanges(data.stages)) {
        onClose();
        return;
      }

      const payload = data.stages.map((item, index) => ({
        ...(item.stageId ? { id: item.stageId } : {}),
        name: item.name.trim(),
        color: item.color,
        position: index + 1
      }));

      await saveStages(pipelineId, payload);

      toast?.current?.show({
        life: 3000,
        severity: "success",
        summary: "Sucesso!",
        detail: "Etapas salvas com sucesso!"
      });

      onSuccess();
      onClose();
    } catch (e: any) {
      toast?.current?.show({
        life: 5000,
        severity: "error",
        summary: "Erro",
        detail:
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          "Erro ao salvar etapas."
      });
    }
  });

  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result;

    if (!destination) return;
    if (source.index === destination.index) return;

    move(source.index, destination.index);
  };

  useEffect(() => {
    reset({
      stages: stages.map((stage, index) => ({
        stageId: stage.id,
        tempId: String(stage.id),
        name: stage.name ?? "",
        color: stage.color || "#6B7280",
        position: stage.position ?? index + 1,
        isNew: false
      }))
    });
  }, [stages, reset]);

  return (
    <>
      <Container>
        <ListStages>
          <span className="helperText">Arraste para reordenar</span>

          <div className="divisor">
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="stages-list">
                {(dropProvided) => (
                  <div
                    className="listStages"
                    ref={dropProvided.innerRef}
                    {...dropProvided.droppableProps}
                  >
                    {fields.map((item, index) => (
                      <Draggable
                        key={item.tempId}
                        draggableId={item.tempId}
                        index={index}
                      >
                        {(dragProvided) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...dragProvided.dragHandleProps}
                            className="stageRow"
                          >
                            <Controller
                              control={control}
                              name={`stages.${index}.color`}
                              render={({ field }) => (
                                <>
                                  <button
                                    type="button"
                                    className="colorDotButton no-hover-button"
                                    onClick={() =>
                                      openColorPicker(fields[index].tempId)
                                    }
                                  >
                                    <span
                                      className="colorDot"
                                      style={{
                                        backgroundColor:
                                          field.value || "#6B7280"
                                      }}
                                    />
                                    <input
                                      ref={(el) =>
                                        (colorInputRefs.current[
                                          fields[index].tempId
                                        ] = el)
                                      }
                                      type="color"
                                      className="hiddenColorInput"
                                      value={field.value || "#6B7280"}
                                      onChange={(e) =>
                                        field.onChange(e.target.value)
                                      }
                                    />
                                  </button>
                                </>
                              )}
                            />

                            <div className="stageInputWrapper">
                              <Controller
                                control={control}
                                name={`stages.${index}.name`}
                                render={({ field }) => (
                                  <InputText
                                    {...field}
                                    placeholder="Nome da etapa"
                                    className="inlineInput"
                                  />
                                )}
                              />

                              {errors.stages?.[index]?.name && (
                                <span className="inlineErrors">
                                  Nome da etapa é obrigatório
                                </span>
                              )}
                            </div>

                            <button
                              type="button"
                              className="deleteButton no-hover-button"
                              onClick={() => handleRemoveStage(index)}
                            >
                              <i className="pi pi-trash" />
                            </button>
                          </div>
                        )}
                      </Draggable>
                    ))}

                    {dropProvided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            <div className="addStageButton" onClick={startCreateStage}>
              <i className="pi pi-plus" style={{ fontSize: "14px" }} />
              Adicionar etapa
            </div>
          </div>

          <div className="buttons">
            <Button
              label="VOLTAR"
              className="no-hover-button"
              onClick={onClose}
            />
            <Button
              label="SALVAR"
              onClick={handleSaveAll}
              style={{
                background: user.plataform.mainColor
                  ? user.plataform.mainColor
                  : "#2624CD",
                borderColor: user.plataform.mainColor
                  ? user.plataform.mainColor
                  : "#2624CD",
                color: "#fff"
              }}
            />
          </div>
        </ListStages>
      </Container>
    </>
  );
};

export default UpdateStage;
